import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { HiCheckCircle, HiArrowUpRight, HiOutlineChatBubbleLeftRight } from 'react-icons/hi2';
import Swal from 'sweetalert2';
import { getProducts } from '../data/productsData';
import { saveEnquiry } from '../data/enquiriesData';

export default function GeneratorSales() {
  const [activeTab, setActiveTab] = useState('Diesel Generators');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [modalData, setModalData] = useState({
    name: '', phone: '', email: '', city: '', capacity: '', message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [productsList, setProductsList] = useState([]);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    const loadProducts = async () => {
      const prods = await getProducts();
      setProductsList(prods);
    };
    loadProducts();
  }, []);

  const handleEnquireProduct = (product) => {
    setSelectedProduct(product);
    setModalData({
      name: '',
      phone: '',
      email: '',
      city: '',
      capacity: product.specs['Power Rating'] || product.specs['Capacity'] || '',
      message: ''
    });
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();

    const phoneDigits = modalData.phone.trim();
    if (!/^\d{10}$/.test(phoneDigits)) {
      Swal.fire({
        title: 'Invalid Phone Number',
        text: 'Please enter a valid 10-digit number.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(modalData.email.trim())) {
      Swal.fire({
        title: 'Invalid Email Address',
        text: 'Please enter a valid email address.',
        icon: 'warning',
        confirmButtonColor: '#0F5A3A'
      });
      return;
    }

    setIsSubmitted(true);

    const newEnquiry = {
      id: `enq-${Date.now()}`,
      name: modalData.name,
      phone: modalData.phone,
      email: modalData.email,
      productId: selectedProduct.id,
      productName: selectedProduct.name,
      city: modalData.city,
      capacity: modalData.capacity,
      message: modalData.message || `Interested in ${selectedProduct.name}`,
      type: 'sales',
      status: 'pending',
      date: new Date().toISOString().replace('T', ' ').slice(0, 16)
    };

    try {
      await saveEnquiry(newEnquiry, true);
      Swal.fire({
        title: 'Sales Enquiry Received!',
        text: `Thank you, ${modalData.name}. We have logged your request for the "${selectedProduct.name}". Our sales engineering team will reach out within 2 hours.`,
        icon: 'success',
        confirmButtonColor: '#0F5A3A'
      });
      setSelectedProduct(null);
    } catch (err) {
      Swal.fire('Error', 'Failed to submit enquiry. Please try again.', 'error');
    } finally {
      setIsSubmitted(false);
    }
  };

  const filteredProducts = productsList.filter(p => p.category === activeTab);

  return (
    <div className="page-enter bg-light font-sans pt-16">

      {/* Hero */}
      <section
        className="relative text-white py-28 overflow-hidden bg-cover bg-center"
        style={{ backgroundImage: `linear-gradient(105deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.65) 50%, rgba(0, 0, 0, 0.3) 100%), url('/Generator Solutions.png')` }}
      >
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-20">
          <span className="eyebrow">Equipment Catalog</span>
          <h1 className="text-4xl md:text-6xl font-display font-extrabold mb-5 leading-tight">
            Generator Sales <span className="text-gradient-gold">& Distribution</span>
          </h1>
          <p className="text-base text-white/65 max-w-2xl leading-relaxed">
            Explore our premium range of silent, diesel, and air-cooled generator systems. Built to CPCB-IV norms and fully backed by Green Gusto dealer warranty.
          </p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-3.5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center gap-2 text-xs text-body-muted">
          <span>Home</span>
          <span className="text-border-dark">/</span>
          <span>Generator Solutions</span>
          <span className="text-border-dark">/</span>
          <span className="font-semibold text-primary">Sales</span>
        </div>
      </div>

      {/* Product Catalog */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-12">
          <span className="eyebrow">Premium Catalog</span>
          <h2 className="text-3xl md:text-4xl font-display font-extrabold text-heading mb-4">Equipment Range By Category</h2>
          <p className="text-base text-body">
            Select a category to view high-performance generator units with detailed specifications.
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {['Diesel Generators', 'Silent Generators', 'Air-cooled Generators'].map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveTab(cat)}
              className={`px-6 py-3 rounded-full text-xs font-bold uppercase tracking-wider transition-all duration-300 ${activeTab === cat
                  ? 'bg-primary text-white shadow-premium'
                  : 'bg-white border border-border text-body hover:border-primary/30 hover:text-primary shadow-card'
                }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((prod) => (
            <div key={prod.id} className="product-card group">
              <Link to={`/generator-product/${prod.id}`} className="block relative overflow-hidden bg-light-section border-b border-border aspect-video">
                <img src={prod.image} alt={prod.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-heading/5 group-hover:bg-heading/0 transition-colors" />
              </Link>

              <div className="p-6 flex-grow flex flex-col">
                <span className="badge-primary text-[9px] mb-3 w-fit">{prod.category}</span>
                <h3 className="text-xs font-bold text-heading uppercase tracking-wide mb-1.5 line-clamp-1 group-hover:text-primary transition-colors">
                  {prod.name}
                </h3>
                <p className="text-xs text-body leading-relaxed mb-4">{prod.desc}</p>
                {prod.specs && Object.keys(prod.specs).length > 0 && (
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    {Object.entries(prod.specs).map(([k, v]) => (
                      <div key={k} className="bg-light-section rounded-lg px-2.5 py-1.5">
                        <span className="text-[9px] text-body-muted block uppercase tracking-wide truncate">{k}</span>
                        <span className="text-xs font-bold text-primary block truncate mt-0.5">{v}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex flex-col gap-2">
                  <Link
                    to={`/generator-product/${prod.id}`}
                    className="btn-secondary w-full justify-center text-[10px] py-2.5 rounded-xl"
                  >
                    View Product
                  </Link>
                  <button
                    onClick={() => handleEnquireProduct(prod)}
                    className="btn-primary w-full justify-center text-[10px] py-2.5 rounded-xl"
                  >
                    Enquire Product
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Specs Comparison Table */}
      <section className="py-20 bg-light-section border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="eyebrow">Quick Guide</span>
            <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading">Range Specifications at a Glance</h2>
          </div>

          <div className="bg-white border border-border rounded-2xl overflow-hidden shadow-card">
            <table className="w-full text-left">
              <thead className="bg-primary text-white">
                <tr>
                  <th className="p-5 text-xs font-bold uppercase tracking-wide">Generator Category</th>
                  <th className="p-5 text-xs font-bold uppercase tracking-wide">Typical Capacity Range</th>
                  <th className="p-5 text-xs font-bold uppercase tracking-wide">Noise Level (1 Meter)</th>
                  <th className="p-5 text-xs font-bold uppercase tracking-wide">Typical Use Cases</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-sm">
                <tr className="hover:bg-light-section transition-colors">
                  <td className="p-5 font-bold text-heading">Diesel Generators</td>
                  <td className="p-5 font-bold text-accent">10 kVA to 3000 kVA</td>
                  <td className="p-5 text-body">&lt; 75 dBA (CPCB-IV standard)</td>
                  <td className="p-5 text-body">Heavy industrial plants, cellular network setups, server rooms, real estate</td>
                </tr>
                <tr className="hover:bg-light-section transition-colors">
                  <td className="p-5 font-bold text-heading">Silent Generators</td>
                  <td className="p-5 font-bold text-accent">10 kVA to 1500 kVA</td>
                  <td className="p-5 font-bold text-primary">&lt; 65 dBA – Super Silent</td>
                  <td className="p-5 text-body">Hospitals, star hotels, shopping malls, high-rise residential complexes</td>
                </tr>
                <tr className="hover:bg-light-section transition-colors">
                  <td className="p-5 font-bold text-heading">Air-cooled Generators</td>
                  <td className="p-5 font-bold text-accent">5 kVA to 50 kVA</td>
                  <td className="p-5 text-body">&lt; 75 dBA</td>
                  <td className="p-5 text-body">Fuel pumps, dairy chillers, standalone retail outlets, bank ATMs</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Why Buy + Form */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10">
        <div className="w-full mb-16">
          <span className="eyebrow">Our Value Proposition</span>
          <h2 className="text-2xl md:text-3xl font-display font-extrabold text-heading mb-5">Why Source Generator Equipment From Green Gusto?</h2>
          <p className="text-sm text-body leading-relaxed mb-8">
            Sourcing critical emergency machinery requires high service commitment and dealer certifications. We make sure your investment is safe, protected, and fully backed by local engineers.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { title: 'Dealer-Backed', desc: '100% official partner warranty on engine and alternators.' },
              { title: 'AMC Support', desc: 'Scheduled maintenance plans available directly post-purchase.' },
              { title: 'Fast Dispatch', desc: 'Standard setups shipped and installed in 7–14 business days.' },
            ].map((v) => (
              <div key={v.title} className="bg-white border border-border rounded-2xl p-5 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 group">
                <HiCheckCircle className="w-5 h-5 text-accent mb-3" />
                <h4 className="text-xs font-bold text-heading uppercase mb-2">{v.title}</h4>
                <p className="text-xs text-body leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product Enquiry Modal */}
      {selectedProduct && createPortal(
        <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-sm">
          <div className="bg-white rounded-[24px] shadow-2xl max-w-3xl w-full overflow-hidden border border-slate-100 relative flex flex-col md:flex-row">

            {/* Close Button */}
            <button
              onClick={() => setSelectedProduct(null)}
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500 hover:text-slate-800 transition-colors z-20 text-xs font-bold"
            >
              ✕
            </button>

            {/* Left Side: Product Info */}
            <div className="w-full md:w-1/2 p-6 md:p-7 flex flex-col justify-between bg-slate-50">
              <div>
                <div className="relative rounded-xl overflow-hidden aspect-video mb-4 border border-slate-200/60 shadow-sm bg-white">
                  <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-cover" />
                </div>
                <span className="badge-primary text-[9px] uppercase tracking-wider mb-2 w-fit">{selectedProduct.category}</span>
                <h3 className="text-lg md:text-xl font-display font-extrabold text-heading mb-2 leading-tight">{selectedProduct.name}</h3>
                <p className="text-[11px] text-body leading-relaxed mb-4 line-clamp-3">{selectedProduct.desc}</p>
              </div>

              <div className="border-t border-slate-200/80 pt-4">
                <span className="text-[9px] font-bold text-primary uppercase tracking-widest block mb-2.5">Key Equipment Features</span>
                <div className="grid grid-cols-1 gap-1.5">
                  {Object.entries(selectedProduct.specs || {}).slice(0, 3).map(([key, val]) => (
                    <div key={key} className="flex items-center gap-2 text-xs text-body font-semibold">
                      <HiCheckCircle className="w-3.5 h-3.5 text-accent flex-shrink-0" />
                      <span className="text-[11px]">{key}: <strong className="text-heading font-bold">{val}</strong></span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Side: The Enquiry Form (Charcoal Background) */}
            <div className="w-full md:w-1/2 bg-charcoal text-white p-6 md:p-7 flex flex-col justify-center">
              <div className="flex items-center gap-2 mb-1.5">
                <HiOutlineChatBubbleLeftRight className="w-3.5 h-3.5 text-accent" />
                <span className="text-[9px] font-bold text-accent uppercase tracking-widest block">Product Enquiry</span>
              </div>
              <h3 className="text-lg font-display font-extrabold text-white mb-0.5">Get a Quote</h3>
              <p className="text-[11px] text-white/60 mb-4 leading-relaxed">
                Complete your contact details to receive a custom quote for the <strong className="text-white">{selectedProduct.name}</strong>.
              </p>

              <form onSubmit={handleModalSubmit} className="space-y-2.5">
                <div>
                  <label className="block text-[8px] text-white/50 mb-0.5 font-bold uppercase tracking-widest">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={modalData.name}
                    onChange={(e) => setModalData({ ...modalData, name: e.target.value })}
                    placeholder="Enter your name"
                    className="input-dark text-xs py-2 w-full bg-white/5 border-white/10"
                  />
                </div>
                <div>
                  <label className="block text-[8px] text-white/50 mb-0.5 font-bold uppercase tracking-widest">Contact Number</label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    value={modalData.phone}
                    onChange={(e) => setModalData({ ...modalData, phone: e.target.value })}
                    placeholder="e.g. +91 98765 43210"
                    className="input-dark text-xs py-2 w-full bg-white/5 border-white/10"
                  />
                </div>
                <div>
                  <label className="block text-[8px] text-white/50 mb-0.5 font-bold uppercase tracking-widest">Email Address</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={modalData.email}
                    onChange={(e) => setModalData({ ...modalData, email: e.target.value })}
                    placeholder="name@company.com"
                    className="input-dark text-xs py-2 w-full bg-white/5 border-white/10"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[8px] text-white/50 mb-0.5 font-bold uppercase tracking-widest">City</label>
                    <input
                      type="text"
                      name="city"
                      required
                      value={modalData.city}
                      onChange={(e) => setModalData({ ...modalData, city: e.target.value })}
                      placeholder="e.g. Aurangabad"
                      className="input-dark text-xs py-2 w-full bg-white/5 border-white/10"
                    />
                  </div>
                  <div>
                    <label className="block text-[8px] text-white/50 mb-0.5 font-bold uppercase tracking-widest">Capacity</label>
                    <input
                      type="text"
                      name="capacity"
                      required
                      value={modalData.capacity}
                      onChange={(e) => setModalData({ ...modalData, capacity: e.target.value })}
                      placeholder="e.g. 1000 kVA"
                      className="input-dark text-xs py-2 w-full bg-white/10 border-white/10"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-[8px] text-white/50 mb-0.5 font-bold uppercase tracking-widest">Message Details</label>
                  <textarea
                    name="message"
                    required
                    value={modalData.message}
                    onChange={(e) => setModalData({ ...modalData, message: e.target.value })}
                    rows={2}
                    placeholder="Ask about pricing, warranty, shipping timeline..."
                    className="input-dark text-xs py-2 w-full bg-white/5 border-white/10 resize-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={isSubmitted}
                  className="btn-green rounded-xl w-full justify-center py-3 text-xs disabled:opacity-60 mt-1"
                >
                  {isSubmitted ? 'Submitting...' : 'Request a Quote'}
                  <HiArrowUpRight className="w-3 h-3" />
                </button>
              </form>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
